import { ScannerScheduledDowntimeAPI } from '@API/data-services/scanner-scheduled-downtime-service'
import { RootState } from '@AppState/redux-store'
import {
  DayOfWeek,
  ResponseError,
  ScheduledDowntime,
  ScheduledDowntimeKind,
  ScheduledDowntimeUpsertRequest,
} from '@Generated/back-office-client/src'
import { Button, Dialog, Flex, RadioGroup, Text, TextArea, TextField } from '@radix-ui/themes'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { fromDateTimeLocalInputValue, toDateTimeLocalInputValue } from '@Components/util/utils'
import { DAY_ORDER, formatDayOfWeek, isValidHHmm } from './types'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  editTarget: ScheduledDowntime | null
}

type FormErrors = {
  daysOfWeek?: string
  startTime?: string
  endTime?: string
  startsAt?: string
  endsAt?: string
}

export const UpsertDrawer = ({ open, onOpenChange, editTarget }: Props) => {
  const queryClient = useQueryClient()
  const { activeCustomerMetadata } = useSelector((state: RootState) => state.users)
  const timezone = activeCustomerMetadata?.timezone ?? 'UTC'

  const [kind, setKind] = useState<ScheduledDowntimeKind>(ScheduledDowntimeKind.RECURRING)
  const [daysOfWeek, setDaysOfWeek] = useState<DayOfWeek[]>([])
  const [startTime, setStartTime] = useState('')
  const [endTime, setEndTime] = useState('')
  const [startsAt, setStartsAt] = useState('')
  const [endsAt, setEndsAt] = useState('')
  const [reason, setReason] = useState('')
  const [errors, setErrors] = useState<FormErrors>({})
  const [submitError, setSubmitError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) {
      return
    }
    if (editTarget) {
      setKind(editTarget.kind)
      setDaysOfWeek(editTarget.daysOfWeek ?? [])
      setStartTime(editTarget.startTime ? editTarget.startTime.slice(0, 5) : '')
      setEndTime(editTarget.endTime ? editTarget.endTime.slice(0, 5) : '')
      setStartsAt(toDateTimeLocalInputValue(timezone, editTarget.startsAt))
      setEndsAt(toDateTimeLocalInputValue(timezone, editTarget.endsAt))
      setReason(editTarget.reason ?? '')
    } else {
      setKind(ScheduledDowntimeKind.RECURRING)
      setDaysOfWeek([])
      setStartTime('')
      setEndTime('')
      setStartsAt('')
      setEndsAt('')
      setReason('')
    }
    setErrors({})
    setSubmitError(null)
  }, [open, editTarget, timezone])

  const handleKindChange = (next: ScheduledDowntimeKind) => {
    setKind(next)
    if (next === ScheduledDowntimeKind.RECURRING) {
      setStartsAt('')
      setEndsAt('')
    } else {
      setDaysOfWeek([])
      setStartTime('')
      setEndTime('')
    }
    setErrors({})
  }

  const toggleDay = (day: DayOfWeek) => {
    setDaysOfWeek((prev) => (prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]))
  }

  const validate = (): FormErrors => {
    const errors: FormErrors = {}
    if (kind === ScheduledDowntimeKind.RECURRING) {
      if (daysOfWeek.length === 0) {
        errors.daysOfWeek = 'Select at least one day.'
      }
      if (!isValidHHmm(startTime)) {
        errors.startTime = 'Required (HH:mm).'
      }
      if (!isValidHHmm(endTime)) {
        errors.endTime = 'Required (HH:mm).'
      }
      if (!errors.startTime && !errors.endTime && startTime === endTime) {
        errors.endTime = 'End time must differ from start time.'
      }
    } else {
      if (!startsAt) {
        errors.startsAt = 'Required.'
      }
      if (!endsAt) {
        errors.endsAt = 'Required.'
      }
      if (!errors.startsAt && !errors.endsAt && new Date(endsAt) <= new Date(startsAt)) {
        errors.endsAt = 'End must be after start.'
      }
    }
    return errors
  }

  const buildRequest = (): ScheduledDowntimeUpsertRequest => {
    if (kind === ScheduledDowntimeKind.RECURRING) {
      return {
        kind,
        daysOfWeek,
        startTime,
        endTime,
        startsAt: null,
        endsAt: null,
        reason,
      }
    }
    return {
      kind,
      daysOfWeek: null,
      startTime: null,
      endTime: null,
      startsAt: new Date(fromDateTimeLocalInputValue(timezone, startsAt)),
      endsAt: new Date(fromDateTimeLocalInputValue(timezone, endsAt)),
      reason,
    }
  }

  const mutation = useMutation({
    mutationFn: async () => {
      const body = buildRequest()
      if (editTarget) {
        return ScannerScheduledDowntimeAPI.update(editTarget.id, body)
      }
      return ScannerScheduledDowntimeAPI.create(body)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scanner-scheduled-downtime'] })
      onOpenChange(false)
    },
    onError: async (err: unknown) => {
      if (err instanceof ResponseError) {
        try {
          const body = await err.response.json()
          setSubmitError(body?.message ?? err.message)
        } catch {
          setSubmitError(err.message)
        }
      } else {
        setSubmitError('An unexpected error occurred.')
      }
    },
  })

  const handleSubmit = () => {
    const v = validate()
    setErrors(v)
    if (Object.keys(v).length > 0) {
      return
    }
    setSubmitError(null)
    mutation.mutate()
  }

  const title = useMemo(
    () => (editTarget ? 'Edit Downtime Window' : 'Add Downtime Window'),
    [editTarget],
  )

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content maxWidth="560px">
        <Dialog.Title>{title}</Dialog.Title>
        <Dialog.Description size="2" mb="3">
          Times are interpreted in customer timezone ({timezone}).
        </Dialog.Description>

        <Flex direction="column" gap="3">
          <Flex direction="column" gap="1">
            <Text size="2" weight="bold">
              Kind
            </Text>
            <RadioGroup.Root
              value={kind}
              onValueChange={(v) => handleKindChange(v as ScheduledDowntimeKind)}>
              <Flex gap="3">
                <Text as="label" size="2">
                  <Flex gap="2" align="center">
                    <RadioGroup.Item value={ScheduledDowntimeKind.RECURRING} /> Recurring
                  </Flex>
                </Text>
                <Text as="label" size="2">
                  <Flex gap="2" align="center">
                    <RadioGroup.Item value={ScheduledDowntimeKind.ONE_OFF} /> One-off
                  </Flex>
                </Text>
              </Flex>
            </RadioGroup.Root>
          </Flex>

          {kind === ScheduledDowntimeKind.RECURRING ? (
            <>
              <Flex direction="column" gap="1">
                <Text size="2" weight="bold">
                  Days of week
                </Text>
                <Flex gap="2" wrap="wrap">
                  {DAY_ORDER.map((day) => {
                    const active = daysOfWeek.includes(day)
                    return (
                      <Button
                        key={day}
                        size="1"
                        variant={active ? 'solid' : 'soft'}
                        color={active ? 'blue' : 'gray'}
                        onClick={() => toggleDay(day)}
                        type="button">
                        {formatDayOfWeek(day)}
                      </Button>
                    )
                  })}
                </Flex>
                {errors.daysOfWeek ? (
                  <Text size="1" color="red">
                    {errors.daysOfWeek}
                  </Text>
                ) : null}
              </Flex>

              <Flex gap="3">
                <Flex direction="column" gap="1" flexGrow="1">
                  <Text size="2" weight="bold">
                    Start time
                  </Text>
                  <TextField.Root
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                  />
                  {errors.startTime ? (
                    <Text size="1" color="red">
                      {errors.startTime}
                    </Text>
                  ) : null}
                </Flex>
                <Flex direction="column" gap="1" flexGrow="1">
                  <Text size="2" weight="bold">
                    End time
                  </Text>
                  <TextField.Root
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                  />
                  {errors.endTime ? (
                    <Text size="1" color="red">
                      {errors.endTime}
                    </Text>
                  ) : null}
                </Flex>
              </Flex>
            </>
          ) : (
            <Flex gap="3">
              <Flex direction="column" gap="1" flexGrow="1">
                <Text size="2" weight="bold">
                  Starts at
                </Text>
                <TextField.Root
                  type="datetime-local"
                  value={startsAt}
                  onChange={(e) => setStartsAt(e.target.value)}
                />
                {errors.startsAt ? (
                  <Text size="1" color="red">
                    {errors.startsAt}
                  </Text>
                ) : null}
              </Flex>
              <Flex direction="column" gap="1" flexGrow="1">
                <Text size="2" weight="bold">
                  Ends at
                </Text>
                <TextField.Root
                  type="datetime-local"
                  value={endsAt}
                  onChange={(e) => setEndsAt(e.target.value)}
                />
                {errors.endsAt ? (
                  <Text size="1" color="red">
                    {errors.endsAt}
                  </Text>
                ) : null}
              </Flex>
            </Flex>
          )}

          <Flex direction="column" gap="1">
            <Text size="2" weight="bold">
              Reason (optional)
            </Text>
            <TextArea
              placeholder="e.g. Nightly maintenance window"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Flex>

          {submitError ? (
            <Text size="2" color="red">
              {submitError}
            </Text>
          ) : null}

          <Flex gap="3" justify="end" mt="2">
            <Dialog.Close>
              <Button variant="soft" color="gray" type="button">
                Cancel
              </Button>
            </Dialog.Close>
            <Button onClick={handleSubmit} loading={mutation.isPending}>
              {editTarget ? 'Save' : 'Create'}
            </Button>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  )
}
